import { useState, useEffect, useMemo } from 'react';
import { X, Save, Mail, Phone, Linkedin, MapPin, Building2, Users, Target, Shield, Loader as Loader2, MessageSquare, ArrowRight, StickyNote, GitBranch, Search, UserMinus, TriangleAlert as AlertTriangle, User, PenLine, Crosshair, Sparkles, Zap } from 'lucide-react';
import { useApi } from '../../../lib/useApi';
import { logAccountActivity } from '../../../lib/activityLog';
import type { Contact, ContactUseCaseScore, BuyingCommitteeRole, SeniorityLevel, OutboundPriority, AccountDepartment } from '../../../types';
import {
  getHighlightFromPriority,
  getEffectivePriority,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_LABELS,
  BUYING_ROLE_LABELS,
  SENIORITY_LABELS,
  PRIORITY_LABELS,
  wouldCreateCycle,
} from '../../../lib/orgChartUtils';

interface ResearchedPersonas {
  personaImpactMap: { persona: string; strategic_accountability: string; executive_risk: string; vendor_capability: string }[];
  valueStatements: { persona: string; statement_text: string; proof_point: string }[];
}

interface Props {
  contact: Contact;
  contacts: Contact[];
  ucScore?: ContactUseCaseScore;
  departments?: AccountDepartment[];
  researchedPersonas?: ResearchedPersonas | null;
  onClose: () => void;
  onUpdate: (contact: Contact) => void;
  onGoToMessaging: () => void;
  onAssignManager: (contactId: string, managerId: string | null) => void;
  onPriorityOverride?: (scoreId: string, priority: OutboundPriority | null, justification: string) => void;
}

const PRIORITY_OPTIONS: { value: OutboundPriority; label: string }[] = [
  { value: 'target_persona', label: 'Target Persona' },
  { value: 'decision_maker', label: 'Decision Maker' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'validator', label: 'Validator' },
  { value: 'low_relevance', label: 'Low Relevance' },
];

const SENIORITY_OPTIONS: { value: SeniorityLevel; label: string }[] = [
  { value: '', label: 'Not set' },
  { value: 'executive', label: 'Executive (C-Suite)' },
  { value: 'executive_leadership', label: 'Executive Leadership (SVP/EVP)' },
  { value: 'senior_leadership', label: 'Senior Leadership (VP)' },
  { value: 'department_leader', label: 'Department Leader (Director/Head)' },
  { value: 'team_manager', label: 'Team Manager' },
  { value: 'individual_contributor', label: 'Individual Contributor' },
];

const BUYING_ROLE_OPTIONS: { value: BuyingCommitteeRole; label: string }[] = [
  { value: '', label: 'Not assigned' },
  { value: 'economic_buyer', label: 'Economic Buyer' },
  { value: 'technical_buyer', label: 'Technical Buyer' },
  { value: 'champion', label: 'Champion' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'validator', label: 'Validator' },
];

export default function ContactDetailPanel({ contact, contacts, ucScore, departments, researchedPersonas, onClose, onUpdate, onGoToMessaging, onAssignManager, onPriorityOverride }: Props) {
  const { apiPut } = useApi();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState(contact.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showManagerPicker, setShowManagerPicker] = useState(false);
  const [managerSearch, setManagerSearch] = useState('');
  const [form, setForm] = useState({
    first_name: contact.first_name,
    last_name: contact.last_name,
    role: contact.role,
    email: contact.email,
    phone: contact.phone,
    linkedin_url: contact.linkedin_url,
    department: contact.department,
    location: contact.location,
    seniority: contact.seniority,
    buying_committee_role: contact.buying_committee_role,
    organisation: contact.organisation,
  });

  useEffect(() => {
    setNotes(contact.notes || '');
    setForm({
      first_name: contact.first_name,
      last_name: contact.last_name,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
      linkedin_url: contact.linkedin_url,
      department: contact.department,
      location: contact.location,
      seniority: contact.seniority,
      buying_committee_role: contact.buying_committee_role,
      organisation: contact.organisation,
    });
    setEditing(false);
    setShowManagerPicker(false);
  }, [contact.id]);

  const effectivePriority = ucScore ? getEffectivePriority(ucScore) : null;
  const highlight = effectivePriority
    ? getHighlightFromPriority(effectivePriority)
    : 'low';
  const colors = HIGHLIGHT_COLORS[highlight];

  const priorityLabel = effectivePriority
    ? PRIORITY_LABELS[effectivePriority] || 'Low Relevance'
    : 'Not Scored';

  const isOverridden = ucScore?.priority_override != null;
  const [editingPriority, setEditingPriority] = useState(false);
  const [overrideValue, setOverrideValue] = useState<OutboundPriority | ''>(ucScore?.priority_override || '');
  const [overrideJustification, setOverrideJustification] = useState(ucScore?.override_justification || '');
  const [savingOverride, setSavingOverride] = useState(false);

  const currentManager = contact.reports_to_id
    ? contacts.find(c => c.id === contact.reports_to_id)
    : null;

  const contactDepartment = useMemo(() => {
    if (!departments || !contact.department) return null;
    return departments.find(d => d.name === contact.department) || null;
  }, [departments, contact.department]);

  const matchedPersonaInsight = useMemo(() => {
    if (!researchedPersonas || !ucScore?.persona_match) return null;
    const matchName = ucScore.persona_match.toLowerCase();
    const impact = researchedPersonas.personaImpactMap.find(
      p => p.persona.toLowerCase() === matchName
    );
    const statements = researchedPersonas.valueStatements.filter(
      v => v.persona.toLowerCase() === matchName
    );
    if (!impact && statements.length === 0) return null;
    return { impact, statements };
  }, [researchedPersonas, ucScore?.persona_match]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { contact: updated } = await apiPut<{ contact: Contact }>(`/v1/contacts/${contact.id}`, form);
      onUpdate(updated);
      setEditing(false);
    } catch {
      // keep the editor open on failure, matching the prior no-op-on-error behaviour
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name: contact.first_name,
      last_name: contact.last_name,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
      linkedin_url: contact.linkedin_url,
      department: contact.department,
      location: contact.location,
      seniority: contact.seniority,
      buying_committee_role: contact.buying_committee_role,
      organisation: contact.organisation,
    });
    setEditing(false);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const { contact: updated } = await apiPut<{ contact: Contact }>(`/v1/contacts/${contact.id}`, { notes });
      onUpdate(updated);
      logAccountActivity(contact.account_id, 'contact_notes_updated', `Updated notes for ${contact.first_name} ${contact.last_name}`);
    } catch {
      // silent, matching the prior no-op-on-error behaviour
    } finally {
      setSavingNotes(false);
    }
  };

  const managerCandidates = contacts
    .filter(c => {
      if (c.id === contact.id) return false;
      if (!managerSearch.trim()) return true;
      const q = managerSearch.toLowerCase();
      return (
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
        c.role.toLowerCase().includes(q) ||
        c.department.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.org_level - b.org_level);

  return (
    <div className="bg-app-surface border-l border-app-line h-full overflow-y-auto">
      <div className="sticky top-0 bg-app-surface border-b border-app-line px-5 py-4 flex items-center justify-between z-10">
        <h3 className="text-sm font-semibold text-app-ink">Contact Details</h3>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button onClick={handleCancel} className="text-xs text-app-muted hover:text-app-ink px-2 py-1">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 text-xs font-medium text-white bg-forest-600 hover:bg-forest-700 px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="text-xs font-medium text-forest-600 hover:text-forest-700 px-2 py-1">
              Edit
            </button>
          )}
          <button onClick={onClose} className="p-1 text-app-muted hover:text-app-muted rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        <button
          onClick={onGoToMessaging}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-none"
        >
          <MessageSquare className="w-4 h-4" />
          Go to Messaging
          <ArrowRight className="w-4 h-4" />
        </button>

        <div className={`p-4 rounded-lg border-2 ${colors.bg} ${colors.border}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
            <span className={`text-xs font-semibold ${colors.text}`}>{HIGHLIGHT_LABELS[highlight]}</span>
          </div>
          {editing ? (
            <div className="space-y-2">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="input-field !text-sm !py-1.5" placeholder="First Name" />
                <input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="input-field !text-sm !py-1.5" placeholder="Last Name" />
              </div>
              <input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="input-field !text-sm !py-1.5" placeholder="Title / Role" />
            </div>
          ) : (
            <>
              <p className="text-base font-bold text-app-ink">{contact.first_name} {contact.last_name}</p>
              <p className="text-sm text-app-muted">{contact.role}</p>
            </>
          )}
        </div>

        {ucScore && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Use Case Relevance</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="p-3 bg-app-canvas rounded-lg text-center">
                <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Relevance</p>
                <p className="text-xl font-bold text-app-ink">{ucScore.use_case_relevance_score}%</p>
              </div>
              <div className="p-3 bg-app-canvas rounded-lg text-center">
                <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Persona Match</p>
                <p className="text-xl font-bold text-app-ink">{ucScore.persona_match_score}%</p>
              </div>
            </div>

            <div className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Outbound Priority</p>
                {onPriorityOverride && (
                  <button
                    onClick={() => setEditingPriority(!editingPriority)}
                    className="flex items-center gap-1 text-[10px] font-medium text-forest-600 hover:text-forest-700 transition-colors"
                  >
                    <PenLine className="w-3 h-3" />
                    {editingPriority ? 'Cancel' : 'Override'}
                  </button>
                )}
              </div>
              <p className={`text-sm font-bold ${colors.text}`}>
                {priorityLabel}
                {isOverridden && (
                  <span className="ml-2 text-[10px] font-medium text-app-warning bg-app-warning/10 px-1.5 py-0.5 rounded">Overridden</span>
                )}
              </p>
              {isOverridden && ucScore.override_justification && !editingPriority && (
                <p className="text-[10px] text-app-muted mt-1 italic">"{ucScore.override_justification}"</p>
              )}
            </div>

            {editingPriority && onPriorityOverride && (
              <div className="p-3 bg-app-surface border border-app-line rounded-lg space-y-2">
                <div>
                  <label className="block text-[10px] font-medium text-app-muted mb-1">New Priority</label>
                  <select
                    value={overrideValue}
                    onChange={e => setOverrideValue(e.target.value as OutboundPriority | '')}
                    className="input-field !text-sm !py-1.5"
                  >
                    <option value="">Use computed ({PRIORITY_LABELS[ucScore.outbound_priority]})</option>
                    {PRIORITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-app-muted mb-1">Justification</label>
                  <textarea
                    value={overrideJustification}
                    onChange={e => setOverrideJustification(e.target.value)}
                    placeholder="Why are you overriding the computed priority?"
                    className="w-full px-3 py-2 text-xs bg-app-canvas border border-app-line rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-forest-300"
                    rows={2}
                  />
                </div>
                <button
                  onClick={async () => {
                    setSavingOverride(true);
                    await onPriorityOverride(
                      ucScore.id,
                      overrideValue || null,
                      overrideJustification
                    );
                    setSavingOverride(false);
                    setEditingPriority(false);
                  }}
                  disabled={savingOverride}
                  className="flex items-center gap-1 text-xs font-medium text-white bg-forest-600 hover:bg-forest-700 px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {savingOverride ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {savingOverride ? 'Saving...' : 'Save Override'}
                </button>
              </div>
            )}

            {ucScore.priority_rationale && (
              <div className="p-3 bg-app-focus/10 border border-app-focus rounded-lg">
                <p className="text-[10px] font-semibold text-app-focus uppercase tracking-wider mb-1">Why This Priority</p>
                <p className="text-xs text-app-focus leading-relaxed">{ucScore.priority_rationale}</p>
              </div>
            )}

            {ucScore.persona_match && (
              <div className="p-3 bg-forest-50 border border-forest-200 rounded-lg">
                <p className="text-[10px] font-semibold text-forest-500 uppercase tracking-wider mb-1">Closest Persona Target</p>
                <p className="text-sm font-medium text-forest-800">{ucScore.persona_match}</p>
              </div>
            )}
          </div>
        )}

        {matchedPersonaInsight && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Selling Intelligence</p>

            {matchedPersonaInsight.impact && (
              <div className="space-y-2">
                <div className="p-3 bg-app-focus/10 border border-app-focus rounded-lg">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Target className="w-3 h-3 text-app-focus" />
                    <p className="text-[10px] font-semibold text-app-focus uppercase tracking-wider">Strategic Accountability</p>
                  </div>
                  <p className="text-xs text-app-focus leading-relaxed">{matchedPersonaInsight.impact.strategic_accountability}</p>
                </div>
                <div className="p-3 bg-app-critical/10 border border-app-critical rounded-lg">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <AlertTriangle className="w-3 h-3 text-app-critical" />
                    <p className="text-[10px] font-semibold text-app-critical uppercase tracking-wider">Executive Risk</p>
                  </div>
                  <p className="text-xs text-app-critical leading-relaxed">{matchedPersonaInsight.impact.executive_risk}</p>
                </div>
                <div className="p-3 bg-forest-50 border border-forest-200 rounded-lg">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Zap className="w-3 h-3 text-forest-500" />
                    <p className="text-[10px] font-semibold text-forest-500 uppercase tracking-wider">Vendor Capability</p>
                  </div>
                  <p className="text-xs text-forest-800 leading-relaxed">{matchedPersonaInsight.impact.vendor_capability}</p>
                </div>
              </div>
            )}

            {matchedPersonaInsight.statements.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Value Statements</p>
                {matchedPersonaInsight.statements.slice(0, 3).map((vs, i) => (
                  <div key={i} className="p-3 bg-app-warning/10 border border-app-warning rounded-lg">
                    <div className="flex items-start gap-1.5 mb-1">
                      <Sparkles className="w-3 h-3 text-app-warning mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-app-warning font-medium leading-relaxed">{vs.statement_text}</p>
                    </div>
                    {vs.proof_point && (
                      <p className="text-[10px] text-app-warning mt-1 pl-[18px] italic">{vs.proof_point}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {contactDepartment && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Department Context</p>
            <div className={`p-3 rounded-lg border ${contactDepartment.is_relevant ? 'bg-forest-50 border-forest-200' : 'bg-app-canvas border-app-line'}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Building2 className={`w-3.5 h-3.5 ${contactDepartment.is_relevant ? 'text-forest-500' : 'text-app-muted'}`} />
                <span className={`text-xs font-semibold ${contactDepartment.is_relevant ? 'text-forest-800' : 'text-app-ink'}`}>
                  {contactDepartment.name}
                </span>
                {contactDepartment.is_relevant && (
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-forest-100 text-forest-700 rounded">Relevant</span>
                )}
              </div>
              {contactDepartment.relevance_rationale && (
                <p className="text-[10px] text-app-muted leading-relaxed">{contactDepartment.relevance_rationale}</p>
              )}
              {contactDepartment.head_name && (
                <p className="text-[10px] text-app-muted mt-1">
                  Dept Head: {contactDepartment.head_name}{contactDepartment.head_title ? ` - ${contactDepartment.head_title}` : ''}
                </p>
              )}
            </div>
          </div>
        )}

        {contact.is_inferred && (
          <div className="p-3 bg-app-focus/10 border border-app-focus/30 rounded-lg">
            <div className="flex items-center gap-1.5 mb-1">
              <Crosshair className="w-3 h-3 text-app-focus" />
              <p className="text-[10px] font-semibold text-app-focus uppercase tracking-wider">Inferred Contact</p>
            </div>
            <p className="text-xs text-app-focus leading-relaxed">
              This contact was added from executive research. Enrich with real contact details for outreach.
            </p>
          </div>
        )}

        {!ucScore && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="p-3 bg-app-canvas rounded-lg text-center">
              <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Relevance</p>
              <p className="text-xl font-bold text-app-muted">N/A</p>
            </div>
            <div className="p-3 bg-app-canvas rounded-lg text-center">
              <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Persona Match</p>
              <p className="text-xl font-bold text-app-muted">N/A</p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Contact Information</p>
          {editing ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="input-field !text-sm !py-1.5 flex-1" placeholder="Email" />
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="input-field !text-sm !py-1.5 flex-1" placeholder="Phone" />
              </div>
              <div className="flex items-center gap-2">
                <Linkedin className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                <input value={form.linkedin_url} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} className="input-field !text-sm !py-1.5 flex-1" placeholder="LinkedIn URL" />
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                <input value={form.organisation} onChange={e => setForm(f => ({ ...f, organisation: e.target.value }))} className="input-field !text-sm !py-1.5 flex-1" placeholder="Organisation" />
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="input-field !text-sm !py-1.5 flex-1" placeholder="Location" />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {contact.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-app-muted" />
                  <a href={`mailto:${contact.email}`} className="text-sm text-app-focus hover:text-app-focus">{contact.email}</a>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-app-muted" />
                  <span className="text-sm text-app-ink">{contact.phone}</span>
                </div>
              )}
              {contact.linkedin_url && (
                <div className="flex items-center gap-2">
                  <Linkedin className="w-3.5 h-3.5 text-app-muted" />
                  <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-app-focus hover:text-app-focus truncate">{contact.linkedin_url}</a>
                </div>
              )}
              {contact.organisation && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-app-muted" />
                  <span className="text-sm text-app-ink">{contact.organisation}</span>
                </div>
              )}
              {contact.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-app-muted" />
                  <span className="text-sm text-app-ink">{contact.location}</span>
                </div>
              )}
              {!contact.email && !contact.phone && !contact.linkedin_url && (
                <p className="text-xs text-app-muted italic">No contact details provided</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Org Position</p>
          {editing ? (
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] font-medium text-app-muted mb-1">Department</label>
                <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} className="input-field !text-sm !py-1.5" placeholder="Department" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-app-muted mb-1">Seniority</label>
                <select value={form.seniority} onChange={e => setForm(f => ({ ...f, seniority: e.target.value as SeniorityLevel }))} className="input-field !text-sm !py-1.5">
                  {SENIORITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-app-muted mb-1">Buying Committee Role</label>
                <select value={form.buying_committee_role} onChange={e => setForm(f => ({ ...f, buying_committee_role: e.target.value as BuyingCommitteeRole }))} className="input-field !text-sm !py-1.5">
                  {BUYING_ROLE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {contact.department && (
                <div className="flex items-center gap-2">
                  <Target className="w-3.5 h-3.5 text-app-muted" />
                  <span className="text-sm text-app-ink">{contact.department}</span>
                </div>
              )}
              {contact.seniority && (
                <div className="flex items-center gap-2">
                  <Users className="w-3.5 h-3.5 text-app-muted" />
                  <span className="text-sm text-app-ink">{SENIORITY_LABELS[contact.seniority] || contact.seniority}</span>
                </div>
              )}
              {contact.buying_committee_role && (
                <div className="flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-app-muted" />
                  <span className="text-sm text-app-ink">{BUYING_ROLE_LABELS[contact.buying_committee_role]}</span>
                </div>
              )}
              <div className="text-xs text-app-muted">
                Org placement confidence: {contact.org_placement_confidence}%
              </div>
            </div>
          )}

          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-medium text-app-muted">Reports to</span>
              <button
                onClick={() => setShowManagerPicker(!showManagerPicker)}
                className="flex items-center gap-1 text-[10px] font-medium text-forest-600 hover:text-forest-700 transition-colors"
              >
                <GitBranch className="w-3 h-3" />
                Change
              </button>
            </div>
            {currentManager ? (
              <div className="flex items-center gap-2 p-2 bg-app-canvas rounded-lg">
                <div className="w-6 h-6 rounded bg-app-line flex items-center justify-center flex-shrink-0">
                  <User className="w-3 h-3 text-app-muted" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-app-ink truncate">{currentManager.first_name} {currentManager.last_name}</p>
                  <p className="text-[10px] text-app-muted truncate">{currentManager.role}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-app-muted italic">No manager (top level)</p>
            )}

            {showManagerPicker && (
              <div className="mt-2 border border-app-line rounded-lg bg-app-surface shadow-none overflow-hidden">
                <div className="p-2 border-b border-app-line">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-app-muted" />
                    <input
                      type="text"
                      value={managerSearch}
                      onChange={e => setManagerSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-7 pr-2 py-1.5 text-xs bg-app-canvas border border-app-line rounded-lg focus:outline-none focus:ring-1 focus:ring-forest-300"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-app-line">
                  <button
                    onClick={() => {
                      onAssignManager(contact.id, null);
                      setShowManagerPicker(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-app-canvas transition-colors ${!contact.reports_to_id ? 'bg-forest-50' : ''}`}
                  >
                    <UserMinus className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                    <span className="text-xs text-app-ink">No Manager (Top Level)</span>
                    {!contact.reports_to_id && <span className="ml-auto text-[9px] font-semibold text-forest-600">Current</span>}
                  </button>
                  {managerCandidates.map(c => {
                    const isCurrent = contact.reports_to_id === c.id;
                    const cycleRisk = wouldCreateCycle(contacts, contact.id, c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => {
                          if (cycleRisk) return;
                          onAssignManager(contact.id, c.id);
                          setShowManagerPicker(false);
                        }}
                        disabled={cycleRisk}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                          cycleRisk ? 'opacity-40 cursor-not-allowed' : isCurrent ? 'bg-forest-50 hover:bg-forest-100' : 'hover:bg-app-canvas'
                        }`}
                      >
                        <User className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-app-ink truncate">{c.first_name} {c.last_name}</p>
                          <p className="text-[10px] text-app-muted truncate">{c.role}</p>
                        </div>
                        {isCurrent && <span className="text-[9px] font-semibold text-forest-600 flex-shrink-0">Current</span>}
                        {cycleRisk && <AlertTriangle className="w-3 h-3 text-app-critical flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <StickyNote className="w-3.5 h-3.5 text-app-muted" />
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider">Notes</p>
            </div>
            {notes !== (contact.notes || '') && (
              <button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="flex items-center gap-1 text-[10px] font-medium text-forest-600 hover:text-forest-700 transition-colors disabled:opacity-50"
              >
                {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {savingNotes ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== (contact.notes || '')) handleSaveNotes();
            }}
            placeholder="Birthday, personal interests, preferences, important considerations..."
            className="w-full px-3 py-2.5 text-sm bg-app-canvas border border-app-line rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-forest-300 focus:border-forest-400 transition-colors leading-relaxed"
            rows={4}
          />
          <p className="text-[10px] text-app-muted">Auto-saves when you click away</p>
        </div>
      </div>
    </div>
  );
}
